import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

const values = [
  {
    num: '01',
    en: 'Translation',
    title: '専門性を、届く広告言語に翻訳する',
    desc: '医療・福祉・教育・地域・文化の専門知識は、そのままでは社会に届きません。私たちはその専門性を共感を生む言葉に翻訳し、広告・寝具・映像など届く形に落とし込みます。',
    dot: 'bg-accent-teal',
    color: 'rgba(74,158,142,',
  },
  {
    num: '02',
    en: 'Participation',
    title: '見る人を、語る人に変える',
    desc: '一方的な広告発信は、受け手を「消費者」にとどめます。私たちは、関わる人が自ら語り、広め、参加したくなるコミュニケーション構造を広告設計の段階から筑きます。',
    dot: 'bg-accent-amber',
    color: 'rgba(212,122,46,',
  },
  {
    num: '03',
    en: 'Legacy',
    title: '今の制作物を、未来のブランド資産にする',
    desc: '今日の広告が、明日の信頼になる。私たちは、広告・冊子・Web・映像の制作を単体の「納品」で終わらせず、組織に蓄積され続けるブランド資産として設計します。',
    dot: 'bg-accent-gold',
    color: 'rgba(201,168,76,',
  },
];

export default function ValuesSection() {
  const heading = useScrollAnimation();
  const items = useStaggerAnimation(values.length);

  return (
    <section className="texture-values py-28 md:py-40 bg-warm-white relative overflow-hidden">

      {/* ── 「3つの価値・三角・均衡」をテーマにした装飾 ── */}

      {/* 左上の「三角形の断片」— 3つの価値を示す */}
      <div className="absolute top-0 left-0 pointer-events-none overflow-hidden w-48 h-48 lg:w-80 lg:h-80">
        {/* 三角形の辺（SVGの代わりにdivで） */}
        <div
          className="absolute"
          style={{
            width: 0, height: 0,
            borderLeft: '60px solid transparent',
            borderRight: '60px solid transparent',
            borderBottom: '104px solid rgba(74,158,142,0.06)',
            top: -20, left: -20,
          }}
        />
        <div
          className="absolute hidden lg:block"
          style={{
            width: 0, height: 0,
            borderLeft: '90px solid transparent',
            borderRight: '90px solid transparent',
            borderBottom: '156px solid rgba(74,158,142,0.04)',
            top: -30, left: -30,
          }}
        />
        {/* 三角形の頂点ドット */}
        <div className="absolute w-2 h-2 rounded-full bg-accent-teal/35" style={{ top: 84, left: 40 }} />
        <div className="absolute w-1.5 h-1.5 rounded-full bg-accent-teal/25" style={{ top: 84, left: 80 }} />
        <div className="absolute w-2 h-2 rounded-full bg-accent-teal/30" style={{ top: 40, left: 60 }} />
      </div>

      {/* 右側の「三分割グリッド」— 3つの価値の均衡 */}
      <div className="absolute top-1/2 -right-12 -translate-y-1/2 pointer-events-none hidden lg:block">
        {/* 3つの同心円（各価値に対応） */}
        <div className="absolute rounded-full border border-accent-gold/20" style={{ width: 240, height: 240, right: 0, top: -120 }} />
        <div className="absolute rounded-full border border-accent-teal/14" style={{ width: 380, height: 380, right: -70, top: -190 }} />
        <div className="absolute rounded-full border border-accent-amber/10" style={{ width: 520, height: 520, right: -140, top: -260 }} />
        {/* 中心点 */}
        <div className="absolute w-2 h-2 rounded-full bg-accent-gold/35" style={{ right: 119, top: -1 }} />
      </div>

      {/* 中央の「三角形の頂点」を示す3点 */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 pointer-events-none hidden lg:block">
        {/* 3つの価値を結ぶ三角形の辺（細い線） */}
        <div className="absolute w-px h-20 bg-gradient-to-b from-accent-teal/15 to-transparent" style={{ left: -60, top: 0, transform: 'rotate(-30deg)' }} />
        <div className="absolute w-px h-20 bg-gradient-to-b from-accent-amber/15 to-transparent" style={{ left: 0, top: 0 }} />
        <div className="absolute w-px h-20 bg-gradient-to-b from-accent-gold/15 to-transparent" style={{ left: 60, top: 0, transform: 'rotate(30deg)' }} />
        {/* 頂点ドット */}
        <div className="absolute w-2 h-2 rounded-full bg-accent-teal/30" style={{ left: -64, top: -2 }} />
        <div className="absolute w-2 h-2 rounded-full bg-accent-amber/30" style={{ left: -2, top: -2 }} />
        <div className="absolute w-2 h-2 rounded-full bg-accent-gold/30" style={{ left: 58, top: -2 }} />
      </div>

      {/* 左上のドットグリッド */}
      <div className="absolute top-0 left-0 w-36 h-36 lg:w-64 lg:h-64 pointer-events-none overflow-hidden">
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(212,122,46,0.20) 1.5px, transparent 1.5px)', backgroundSize: '20px 20px' }} />
      </div>

      {/* 各価値に対応した菱形群（左下・中央・右上） */}
      {/* 01 Translation — teal */}
      <div className="absolute bottom-12 left-1/4 pointer-events-none">
        <div className="w-7 h-7 lg:w-12 lg:h-12 border-2 border-accent-teal/30 rotate-45" />
        <div className="absolute w-3.5 h-3.5 lg:w-6 lg:h-6 border border-accent-teal/38 rotate-45" style={{ left: 7, top: 7 }} />
      </div>
      {/* 02 Participation — amber */}
      <div className="absolute top-12 right-1/4 pointer-events-none">
        <div className="w-5 h-5 lg:w-10 lg:h-10 border-2 border-accent-amber/35 rotate-45" />
        <div className="absolute w-2.5 h-2.5 lg:w-5 lg:h-5 border border-accent-amber/42 rotate-45" style={{ left: 5, top: 5 }} />
        <div className="absolute w-1.5 h-1.5 rounded-full bg-accent-amber/35" style={{ left: 7, top: 7 }} />
      </div>
      {/* 03 Legacy — gold */}
      <div className="absolute bottom-8 right-12 pointer-events-none hidden lg:block">
        <div className="w-8 h-8 border border-accent-gold/32 rotate-45" />
        <div className="absolute w-4 h-4 border border-accent-gold/40 rotate-45" style={{ left: 8, top: 8 }} />
      </div>

      {/* 浮遊ドット（3色・均等配置） */}
      <div className="absolute top-1/2 left-1/2 w-4 h-4 rounded-full bg-accent-teal/22 pointer-events-none" />
      <div className="absolute top-[30%] left-[20%] w-3 h-3 rounded-full bg-accent-amber/30 pointer-events-none" />
      <div className="absolute bottom-[25%] right-[30%] w-5 h-5 rounded-full bg-accent-gold/22 pointer-events-none" />
      <div className="absolute top-[65%] left-[35%] w-2 h-2 rounded-full bg-accent-teal/28 pointer-events-none hidden lg:block" />
      <div className="absolute top-[20%] right-[40%] w-2.5 h-2.5 rounded-full bg-accent-amber/25 pointer-events-none hidden lg:block" />

      {/* 右下の細かいドットグリッド */}
      <div className="absolute bottom-0 right-0 w-32 h-32 lg:w-56 lg:h-56 pointer-events-none overflow-hidden">
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(201,168,76,0.22) 1px, transparent 1px)', backgroundSize: '14px 14px' }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        {/* Heading */}
        <div
          ref={heading.ref as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-20 transition-all duration-700"
          style={{ opacity: heading.isVisible ? 1 : 0, transform: heading.isVisible ? 'translateY(0)' : 'translateY(28px)' }}
        >
          <div className="md:col-span-4">
            <div className="flex items-center gap-4 mb-6">
              <span className="block w-6 h-px bg-accent-teal" />
              <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Three Values</span>
            </div>
            <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-3xl md:text-4xl">
              私たちが大切にする、<br />三つのこと。
            </h2>
          </div>
        </div>

        {/* Values list */}
        <div
          ref={items.ref as React.RefObject<HTMLDivElement>}
          className="space-y-0 border-t border-divider"
        >
          {values.map((v, i) => (
            <div
              key={v.num}
              className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 py-12 md:py-16 border-b border-divider group transition-all duration-700"
              style={{
                opacity: items.visibleCount > i ? 1 : 0,
                transform: items.visibleCount > i ? 'translateY(0)' : 'translateY(32px)',
                transitionDelay: `${i * 120}ms`,
              }}
            >
              <div className="md:col-span-2 flex md:flex-col items-baseline md:items-start gap-4 md:gap-2">
                <span className="font-garamond text-4xl font-light text-divider leading-none">{v.num}</span>
                <span className="font-garamond text-xs italic text-muted-light tracking-widest-jp">{v.en}</span>
              </div>
              <div className="md:col-span-4 flex items-start gap-3">
                <span className={`block w-2 h-2 rounded-full flex-shrink-0 mt-2 ${v.dot}`} />
                <h3 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-snug group-hover:text-accent-teal transition-colors duration-300 text-xl">
                  {v.title}
                </h3>
              </div>
              <div className="md:col-span-6">
                <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">{v.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
